import { FormPayload } from "@/app/interface/components";
import { getPersistedUtmParams } from "@/app/utils/utm";

export const ALAN_WHATSAPP_NUMBER = "556198735298";

export interface AdReferralDto {
	source?: string;
	source_url?: string;
	campaign?: string;
	content?: string;
	term?: string;
	occurred_at?: string;
}

export interface AdReferralLeadDto {
	phonenumber?: string;
	name?: string;
	email?: string;
	external_id?: string;
	target_product?: string;
	dealership_name?: string;
	dealership_uf?: string;
	ad_referral?: AdReferralDto;
}

function digitsOnly(value: string): string {
	return (value || "").replace(/\D/g, "");
}

export function normalizeAlanPhone(phone: string): string {
	const digits = digitsOnly(phone);
	if (!digits) return "";
	if (digits.startsWith("55")) return digits;
	return `55${digits}`;
}

export function resolveDealerName(formData: Omit<FormPayload, "registrationNumber">): string {
	const fd = formData as Omit<FormPayload, "registrationNumber"> & {
		nome_loja?: string;
		phone?: string;
	};
	return (fd.nome_loja || fd.dealer || "").toString().trim();
}

export function formatLojaForWhatsApp(dealer?: string): string {
	const raw = (dealer || "").trim();
	if (!raw) return "Kyoto";
	return raw.replace(/^kyoto\s+/i, "").trim() || "Kyoto";
}

export function formatDealershipName(dealer?: string): string {
	const raw = (dealer || "").trim();
	if (!raw) return "Kyoto";
	if (/kyoto/i.test(raw)) return raw;
	return `Kyoto ${raw}`;
}

export function resolveModelName(formData: Omit<FormPayload, "registrationNumber">): string {
	if (!formData.model) return "Não informado";
	const modelStr = formData.model.toString();
	if (
		!modelStr ||
		modelStr === "0" ||
		["NaN", "undefined", "null"].includes(modelStr)
	) {
		return "Não informado";
	}
	return modelStr.trim();
}

export function buildAlanWhatsAppUrl(
	name: string,
	dealer?: string,
	model?: string,
): string {
	const loja = formatLojaForWhatsApp(dealer);
	const modelo = model?.trim() || "Não informado";
	const message = `Olá! Vim do site e meu nome é ${name.trim()}. Tenho interesse no modelo ${modelo} na loja ${loja}.`;
	return `https://wa.me/${ALAN_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

export function redirectToAlanWhatsApp(
	name: string,
	dealer?: string,
	model?: string,
): void {
	if (typeof window === "undefined") return;
	window.location.href = buildAlanWhatsAppUrl(name, dealer, model);
}

function resolveUtmParams(): URLSearchParams {
	let utmParamsString = getPersistedUtmParams();
	if (!utmParamsString && typeof window !== "undefined") {
		utmParamsString = window.location.search;
	}
	return new URLSearchParams(utmParamsString);
}

export function buildAdReferralLeadPayload(
	formData: Omit<FormPayload, "registrationNumber">,
): AdReferralLeadDto {
	const fd = formData as Omit<FormPayload, "registrationNumber"> & {
		phone?: string;
		nome_loja?: string;
	};
	const phone = fd.phone || fd.cellPhone || "";
	const dealer = resolveDealerName(formData);
	const urlParams = resolveUtmParams();

	return {
		phonenumber: normalizeAlanPhone(phone),
		name: formData.name,
		email: formData.email || "",
		target_product: resolveModelName(formData),
		dealership_name: formatDealershipName(dealer),
		dealership_uf: "DF",
		ad_referral: {
			source: urlParams.get("utm_source") || "site",
			source_url:
				typeof window !== "undefined" ? window.location.href : "",
			campaign: urlParams.get("utm_campaign") || "",
			content: urlParams.get("utm_content") || "",
			term: urlParams.get("utm_term") || "",
			occurred_at: new Date().toISOString(),
		},
	};
}

export async function sendToAlan(
	formData: Omit<FormPayload, "registrationNumber">,
	options?: { skipWhatsAppRedirect?: boolean },
): Promise<boolean> {
	try {
		const payload = buildAdReferralLeadPayload(formData);

		const response = await fetch("/api/alan", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(payload),
		});

		const responseText = await response.text();
		let responseData: { error?: string; details?: unknown } = {};
		try {
			responseData = responseText ? JSON.parse(responseText) : {};
		} catch {
			responseData = { error: responseText };
		}

		if (!response.ok) {
			console.error("Erro ao enviar para Alan:", {
				status: response.status,
				error: responseData,
			});
			return false;
		}

		if (!options?.skipWhatsAppRedirect) {
			redirectToAlanWhatsApp(
				formData.name,
				resolveDealerName(formData),
				resolveModelName(formData),
			);
		}

		return true;
	} catch (error) {
		console.error("Erro na integração com Alan:", {
			message:
				error instanceof Error ? error.message : "Erro desconhecido",
			stack: error instanceof Error ? error.stack : undefined,
		});
		return false;
	}
}
